import { Button as MuiButton } from '@material-ui/core'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

/**
 * Custom Button component that extends Material-UI Button with additional features
 * @component
 * @param {Object} props
 * @param {string} [props.width='150px'] - Width of the button
 * @param {string} [props.to] - Route path for Link functionality
 * @param {React.ReactNode} props.children - Button content
 * @param {('only-text'|'text'|'outlined'|'contained')} [props.variant='contained'] - Button variant
 * @param {('default'|'inherit'|'primary'|'secondary')} [props.color='primary'] - Button color
 * @param {Object} [props.style={}] - Additional styles
 * @param {boolean} [props.disabled] - Whether the button is disabled
 * @param {function} [props.onClick] - Click handler function
 * @param {string} [props.className] - Additional CSS class name
 * @param {string} [props.size='medium'] - Button size ('small'|'medium'|'large')
 * @param {React.ReactNode} [props.startIcon] - Icon to display at the start of button
 * @param {React.ReactNode} [props.endIcon] - Icon to display at the end of button
 * @param {string} [props.type='button'] - Button type ('button'|'submit'|'reset')
 * @param {string} [props.id] - Button ID
 * @param {string} [props.name] - Button name
 * @param {string} [props.form] - Form ID that the button belongs to
 * @param {string} [props.value] - Button value
 * @param {boolean} [props.fullWidth] - Whether the button should take full width
 * @param {boolean} [props.disableElevation] - Whether to disable elevation
 * @param {boolean} [props.disableFocusRipple] - Whether to disable focus ripple
 * @param {boolean} [props.disableRipple] - Whether to disable ripple effect
 * @param {boolean} [props.focusVisibleClassName] - Class name for focus visible state
 * @param {string} [props.href] - URL for href functionality
 * @param {string} [props.target] - Target for href link
 * @param {string} [props.rel] - Rel attribute for href link
 * @returns {JSX.Element} Custom Button component
 */
const Button = ({ width = '150px', to, children, variant = 'contained', color = 'primary', style = {}, ...props }) => {
  let buttonStyle = {
    ...style,
    marginRight: 5,
    marginBottom: 5,
    textTransform: 'none',
    minWidth: width,
    ...(variant == 'only-text' && {
      padding: '0px',
      margin: '0px',
      minWidth: 'unset',
    }),
  }

  let buttonProps = {
    variant: variant == 'only-text' ? 'text' : variant,
    color,
    style: buttonStyle,
    ...(to && { component: Link, to: to }),
    ...props,
  }

  return <MuiButton {...buttonProps}>{children}</MuiButton>
}

Button.propTypes = {
  width: PropTypes.string,
  to: PropTypes.string,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['only-text', 'text', 'outlined', 'contained']),
  color: PropTypes.oneOf(['default', 'inherit', 'primary', 'secondary']),
  style: PropTypes.object,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  id: PropTypes.string,
  name: PropTypes.string,
  form: PropTypes.string,
  value: PropTypes.string,
  fullWidth: PropTypes.bool,
  disableElevation: PropTypes.bool,
  disableFocusRipple: PropTypes.bool,
  disableRipple: PropTypes.bool,
  focusVisibleClassName: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
}

export default Button
